import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Phone } from 'lucide-react';

// Default car models (fallback) - will be overridden by props
const defaultCarModels = [
  { name: 'BYD M6', slug: 'byd-m6' },
  { name: 'BYD Atto 1', slug: 'byd-atto-1' },
  { name: 'BYD Seal', slug: 'byd-seal' },
  { name: 'BYD Dolphin', slug: 'byd-dolphin' },
  { name: 'BYD Atto 3', slug: 'byd-atto-3' },
  { name: 'BYD Sealion 7', slug: 'byd-sealion-7' },
  { name: 'DENZA D9', slug: 'byd-denza-d9' },
];

export default function Navbar({ settings, cars, pageContents }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showModels, setShowModels] = useState(false);
  const location = useLocation();

  // Use cars from API if available, otherwise use defaults
  const carModels = cars?.length > 0 
    ? cars.map(car => ({ 
        name: car.name, 
        slug: car.slug, 
        badge: car.is_featured ? 'Terlaris' : null 
      }))
    : defaultCarModels;

  const contactButtonText = settings?.nav_contact_button_text || 'Hubungi Sales';
  const waNumber = settings?.whatsapp_number || '082319804253';
  
  // Dynamic page names from admin
  const simulasiKreditName = pageContents?.['simulasi-kredit']?.page_display_name || 'Simulasi Kredit';

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menu on route change
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  const formatWaNumber = (num) => {
    let n = (num || '082319804253').replace(/\D/g, '');
    if (n.startsWith('0')) n = '62' + n.substring(1);
    return n;
  };

  return (
    <nav data-testid="main-navbar" className={`navbar-glass transition-all duration-200 ${isScrolled ? 'shadow-sm' : ''}`} role="navigation" aria-label="Main navigation">
      <div className="byd-container">
        <div className="flex items-center justify-between h-16 lg:h-20">
          <Link to="/" className="flex items-center gap-2 min-h-[44px] min-w-[44px]" data-testid="navbar-logo" aria-label="Beranda">
            {settings?.logo_url ? (
              <img 
                src={settings.logo_url} 
                alt={settings?.logo_alt || 'BYD Indonesia Logo'} 
                className="h-8 lg:h-10 object-contain"
                width="120"
                height="40"
                loading="eager"
              />
            ) : (
              <>
                <span className="text-xl lg:text-2xl font-bold tracking-tight text-slate-900">{settings?.logo_text || 'BYD'}</span>
                <span className="text-xs lg:text-sm text-slate-500 hidden sm:block">{settings?.logo_subtext || 'Indonesia'}</span>
              </>
            )}
          </Link>

          <div className="hidden lg:flex items-center gap-8">
            <div className="relative" onMouseEnter={() => setShowModels(true)} onMouseLeave={() => setShowModels(false)}>
              <button 
                className="flex items-center gap-1 text-sm font-medium text-slate-700 hover:text-slate-900 transition-colors min-h-[44px] px-2" 
                data-testid="nav-models-dropdown"
                aria-expanded={showModels}
                aria-haspopup="true"
                aria-label="Model kendaraan"
              >
                Model <ChevronDown className={`w-4 h-4 transition-transform ${showModels ? 'rotate-180' : ''}`} aria-hidden="true" />
              </button>
              {showModels && (
                <div className="absolute top-full left-0 mt-2 w-56 bg-white border border-slate-200 shadow-xl animate-fadeInUp" role="menu">
                  {carModels.map((car) => (
                    <Link key={car.slug} to={`/mobil/${car.slug}`} className="flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors min-h-[44px]" data-testid={`nav-model-${car.slug}`} role="menuitem">
                      <span className="text-sm text-slate-700">{car.name}</span>
                      {car.badge && <span className="text-xs px-2 py-0.5 bg-red-500 text-white" aria-label={`${car.name} - ${car.badge}`}>{car.badge}</span>}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link to="/simulasi-kredit" className="text-sm font-medium text-slate-700 hover:text-slate-900 transition-colors" data-testid="nav-simulasi-kredit">{simulasiKreditName}</Link>
            <Link to="/artikel" className="text-sm font-medium text-slate-700 hover:text-slate-900 transition-colors" data-testid="nav-artikel">Artikel</Link>
          </div>

          <div className="hidden lg:flex items-center gap-4">
            <a href={`https://wa.me/${formatWaNumber(waNumber)}?text=Halo%20Bayu,%20saya%20tertarik%20dengan%20mobil%20BYD`} target="_blank" rel="noopener noreferrer" className="btn-primary flex items-center gap-2 text-sm min-h-[44px]" data-testid="nav-contact-btn" aria-label={`${contactButtonText} via WhatsApp`}>
              <Phone className="w-4 h-4" aria-hidden="true" />{contactButtonText}
            </a>
          </div>

          <button onClick={() => setIsOpen(!isOpen)} className="lg:hidden p-3 min-h-[44px] min-w-[44px] flex items-center justify-center" data-testid="mobile-menu-btn" aria-label={isOpen ? 'Tutup menu' : 'Buka menu'} aria-expanded={isOpen}>
            {isOpen ? <X className="w-6 h-6" aria-hidden="true" /> : <Menu className="w-6 h-6" aria-hidden="true" />}
          </button>
        </div>
      </div>

      {isOpen && (
        <div className="lg:hidden bg-white border-t border-slate-200 animate-fadeInUp" data-testid="mobile-menu" role="menu">
          <div className="byd-container py-4 space-y-4">
            <div className="space-y-2">
              <p className="text-label" id="mobile-model-label">Model</p>
              <div className="grid grid-cols-2 gap-2" role="group" aria-labelledby="mobile-model-label">
                {carModels.map((car) => (
                  <Link key={car.slug} to={`/mobil/${car.slug}`} className="flex items-center gap-2 p-3 bg-slate-50 hover:bg-slate-100 transition-colors min-h-[48px]" role="menuitem" aria-label={`Lihat ${car.name}`}>
                    <span className="text-sm text-slate-700">{car.name}</span>
                    {car.badge && <span className="text-xs px-1.5 py-0.5 bg-red-500 text-white" aria-hidden="true">{car.badge}</span>}
                  </Link>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Link to="/simulasi-kredit" className="block p-3 text-sm font-medium text-slate-700 hover:bg-slate-50 min-h-[48px] flex items-center" role="menuitem">{simulasiKreditName}</Link>
              <Link to="/artikel" className="block p-3 text-sm font-medium text-slate-700 hover:bg-slate-50 min-h-[48px] flex items-center" role="menuitem">Artikel</Link>
            </div>
            <a href={`https://wa.me/${formatWaNumber(waNumber)}?text=Halo%20Bayu,%20saya%20tertarik%20dengan%20mobil%20BYD`} target="_blank" rel="noopener noreferrer" className="btn-primary w-full flex items-center justify-center gap-2 text-sm min-h-[48px]" aria-label={`${contactButtonText} via WhatsApp`}>
              <Phone className="w-4 h-4" aria-hidden="true" />{contactButtonText}
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}
